"use client";

import { useState } from "react";

/** useState backed by localStorage, so a selection survives navigation and
 * reloads. Keyed per control (e.g. "fin.range.networth"). SSR-safe: reads
 * storage lazily on first client render. */
export function usePersistedState<T>(
  key: string,
  fallback: T,
): [T, (v: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === "undefined") return fallback;
    try {
      const raw = window.localStorage.getItem(key);
      return raw != null ? (JSON.parse(raw) as T) : fallback;
    } catch {
      return fallback;
    }
  });
  // Supports functional updates like useState's setter.
  const set = (v: T | ((prev: T) => T)) => {
    setValue((prev) => {
      const next = typeof v === "function" ? (v as (p: T) => T)(prev) : v;
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* private mode / quota — in-memory only */
      }
      return next;
    });
  };
  return [value, set];
}

/** useState backed by sessionStorage: survives in-app navigation (e.g. a
 * drilldown round-trip) but resets on a fresh app open — for controls that
 * should always START at their default (like the Trends density toggle)
 * without forgetting mid-session choices. */
export function useSessionState<T>(
  key: string,
  fallback: T,
): [T, (v: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === "undefined") return fallback;
    try {
      const raw = window.sessionStorage.getItem(key);
      return raw != null ? (JSON.parse(raw) as T) : fallback;
    } catch {
      return fallback;
    }
  });
  const set = (v: T | ((prev: T) => T)) => {
    setValue((prev) => {
      const next = typeof v === "function" ? (v as (p: T) => T)(prev) : v;
      try {
        window.sessionStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* private mode / quota — in-memory only */
      }
      return next;
    });
  };
  return [value, set];
}
